import {
  AddIconData,
  FormatAlignCenterIconData,
  FormatAlignLeftIconData,
  FormatAlignRightIconData,
} from "@meiden-connect/material-symbols";
import { Button, Icon } from "@meiden-connect/ui";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import type { StrokePattern, TextAlign } from "@refinement-canvas/domain";
import { strokeDashArray } from "../../../lib/stroke-pattern";
import {
  isValidHexColor,
  normalizeHexColor,
  PEN_THICKNESS_PIXELS,
  type PenThickness,
} from "../../../lib/pen-preference";
import { SHAPE_STROKE_WIDTH_PIXELS, type ShapeStrokeWidth } from "../../../lib/shape-preference";
import {
  clampTextFontSize,
  resolveTextSizeKind,
  TEXT_SIZE_MAX_PX,
  TEXT_SIZE_MIN_PX,
  TEXT_SIZE_PRESET_PX,
  type TextSizeKind,
} from "../../../lib/text-size-preference";
import {
  CustomColorMenu,
  CustomColorPopover,
  type OverlayCloseReason,
} from "./custom-color-controls";

// ツールバーのサブパネルを構成する表示部品群。
// 「次に新規配置するときの既定値」を選ぶツール別パネル（tool-bar.tsx）と、
// 「選択中オブジェクトのスタイル」を変える選択編集パネル（selection-style-panel.tsx）の
// 両方から使うため、この 1 か所へ集約する。
//
// 選択編集パネルは複数オブジェクトを対象にするため、値が割れている（混在している）状態を
// 表現できる必要がある。そのため各部品の value は null を受け付け、null のときは
// 「どの選択肢も強調しない」表示にする。

/**
 * サブパネル 1 枚分の枠。見出しと操作領域を縦に並べる。
 * `testId` はテストから一意に取得するための任意指定で、未指定なら属性を付けない。
 */
export function SubPanel({
  children,
  label,
  testId,
}: {
  readonly children: ReactNode;
  readonly label: string;
  readonly testId?: string | undefined;
}): JSX.Element {
  return (
    <div className="canvas-toolbar-subpanel" aria-label={label} data-testid={testId}>
      <span className="canvas-toolbar-subpanel-label">{label}</span>
      <div className="canvas-toolbar-subpanel-actions">{children}</div>
    </div>
  );
}

/**
 * 親トリガーの中に出す、色の現在値プレビュー。
 *
 * `underline` を指定すると文字色用の下線付き `A`、指定しない場合は色面になる。
 * 「背景なし」（`transparent` / `none`）は透明表現、hex として解釈できない値と
 * value が null（複数選択で値が割れている）ときは混在表示にする。
 * 配置前パネルと選択編集パネルの双方から使い、同じ値表現を 1 か所へ集約する。
 */
export function ColorValuePreview({
  value,
  underline = false,
}: {
  readonly value: string | null;
  readonly underline?: boolean | undefined;
}): JSX.Element {
  const transparent = value === "transparent" || value === "none";
  const hex = value !== null && isValidHexColor(value) ? value : undefined;
  // chooser と同じ hex 判定を使い、非対応の文字列を親だけで CSS 色として解釈しない。
  const neutral = hex === undefined && !transparent;
  return (
    <span
      className={`canvas-selection-style-color${neutral ? " is-mixed" : transparent ? " is-transparent" : ""}${underline ? " is-text" : ""}`}
      style={{
        backgroundColor: !underline ? hex : undefined,
        borderBottomColor: underline ? hex : undefined,
      }}
    >
      {underline ? "A" : neutral ? "—" : ""}
    </span>
  );
}

// 色のサンプル丸ボタン群。
// プリセット色 → カスタム色 → 「＋」追加ボタン の順に並べる。
// 「＋」は明示確定するエディタを開き、カスタム色の右クリックは変更・削除メニューを開く。
// 選択中の丸には外側の青い円形リングを描いて選択状態を強調する（添付イメージ準拠）。
// value が null（値が割れている）ときは、どの丸も選択状態にしない。
export function ColorSwatchList({
  ariaLabel,
  presetColors,
  customColors,
  value,
  onChoose,
  onAddColor,
  onUpdateCustomColor,
  onDeleteCustomColor,
  noneOption,
  allowCustomColorEditing = true,
}: {
  readonly ariaLabel: string;
  readonly presetColors: readonly string[];
  readonly customColors: readonly string[];
  readonly value: string | null;
  readonly onChoose: (color: string) => void;
  readonly onAddColor: (color: string) => void;
  readonly onUpdateCustomColor: (previous: string, next: string) => void;
  readonly onDeleteCustomColor: (color: string) => void;
  // 「なし」（背景なし等）を表す先頭のサンプル。指定時のみ表示する。
  readonly noneOption?: { readonly value: string; readonly label: string } | undefined;
  /**
   * 任意色の追加・変更・削除 UI（「＋」と右クリックメニュー）を出すか（既定 true）。
   * テキスト範囲専用パネルでは false にする。これらはポップオーバーへフォーカスを移すため、
   * 編集中の contentEditable から選択範囲が失われ、適用対象を見失うためである
   * （保存済みカスタム色の選択は従来どおり行える）。
   */
  readonly allowCustomColorEditing?: boolean | undefined;
}): JSX.Element {
  const listRef = useRef<HTMLDivElement | null>(null);
  const [menu, setMenu] = useState<{
    readonly color: string;
    readonly anchorElement: HTMLButtonElement;
  } | null>(null);
  const [editor, setEditor] = useState<{
    readonly mode: "add" | "edit";
    readonly initialColor: string;
    readonly previousColor: string | null;
    readonly anchorElement: HTMLButtonElement;
  } | null>(null);

  const restoreFocus = useCallback((anchorElement: HTMLButtonElement): void => {
    window.requestAnimationFrame(() => {
      if (anchorElement.isConnected) {
        anchorElement.focus();
        return;
      }
      listRef.current
        ?.querySelector<HTMLButtonElement>('[data-testid="custom-color-add"]')
        ?.focus();
    });
  }, []);

  // アンカーが子パネルのスクロールで完全に隠れて閉じた場合は、非表示のアンカーへ
  // フォーカスを戻さない（退避はオーバーレイ側が子のスクロールコンテナへ済ませている）。
  // 参照を固定するのは、オーバーレイ側が close ハンドラを依存に持つ購読を張っており、
  // 毎 render で購読を張り直すと保留中の再計測フレームまで取り消してしまうためである。
  const closeMenu = useCallback(
    (reason: OverlayCloseReason = "dismiss"): void => {
      if (!menu) return;
      const anchorElement = menu.anchorElement;
      setMenu(null);
      if (reason !== "anchor-clipped") restoreFocus(anchorElement);
    },
    [menu, restoreFocus]
  );

  const closeEditor = useCallback(
    (reason: OverlayCloseReason = "dismiss"): void => {
      if (!editor) return;
      const anchorElement = editor.anchorElement;
      setEditor(null);
      if (reason !== "anchor-clipped") restoreFocus(anchorElement);
    },
    [editor, restoreFocus]
  );

  // value が「なし」センチネル（hex でない）の場合に normalizeHexColor へ渡すと別の文字列へ
  // 化けるため、hex のときだけ正規化する。色見本の選択判定にだけ使う。
  const normalizedValue = isValidHexColor(value) ? normalizeHexColor(value) : value;
  // 重複排除のため、プリセットに既に含まれている custom 色は表示しない（loadX で既に正規化済み）。
  const visibleCustom = customColors.filter((c) => !presetColors.includes(c));

  return (
    <div
      ref={listRef}
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        alignItems: "center",
        maxWidth: "440px",
      }}
    >
      {noneOption ? (
        <NoneSwatch
          label={noneOption.label}
          selected={value === noneOption.value}
          onClick={() => onChoose(noneOption.value)}
        />
      ) : null}
      {presetColors.map((color) => (
        <ColorSwatch
          key={`preset-${color}`}
          color={color}
          selected={normalizeHexColor(color) === normalizedValue}
          onClick={() => onChoose(color)}
        />
      ))}
      {visibleCustom.map((color) =>
        allowCustomColorEditing ? (
          <ColorSwatch
            key={`custom-${color}`}
            color={color}
            selected={normalizeHexColor(color) === normalizedValue}
            onClick={() => onChoose(color)}
            title={`${color}（右クリックで変更・削除）`}
            onContextMenu={(event) => {
              event.preventDefault();
              setEditor(null);
              setMenu({
                color,
                anchorElement: event.currentTarget,
              });
            }}
          />
        ) : (
          <ColorSwatch
            key={`custom-${color}`}
            color={color}
            selected={normalizeHexColor(color) === normalizedValue}
            onClick={() => onChoose(color)}
            title={color}
          />
        )
      )}
      {allowCustomColorEditing ? (
        <AddColorSwatch
          expanded={editor?.mode === "add"}
          onOpen={(anchorElement) => {
            const fallback = presetColors[0] ?? "#000000";
            const initialColor = isValidHexColor(value) ? normalizeHexColor(value) : fallback;
            setMenu(null);
            setEditor({
              mode: "add",
              initialColor,
              previousColor: null,
              anchorElement,
            });
          }}
        />
      ) : null}
      {menu ? (
        <CustomColorMenu
          color={menu.color}
          anchorElement={menu.anchorElement}
          onClose={closeMenu}
          onEdit={() => {
            setEditor({
              mode: "edit",
              initialColor: menu.color,
              previousColor: menu.color,
              anchorElement: menu.anchorElement,
            });
            setMenu(null);
          }}
          onDelete={() => {
            const anchorElement = menu.anchorElement;
            onDeleteCustomColor(menu.color);
            setMenu(null);
            restoreFocus(anchorElement);
          }}
        />
      ) : null}
      {editor ? (
        <CustomColorPopover
          mode={editor.mode}
          initialColor={editor.initialColor}
          anchorElement={editor.anchorElement}
          onCancel={closeEditor}
          onCommit={(color) => {
            if (editor.previousColor === null) onAddColor(color);
            else onUpdateCustomColor(editor.previousColor, color);
            closeEditor();
          }}
        />
      ) : null}
    </div>
  );
}

// 色サンプル 1 件分。<input type="radio"> をベースに視覚は丸い色見本へ差し替える。
export function ColorSwatch({
  color,
  selected,
  onClick,
  title,
  onContextMenu,
}: {
  readonly color: string;
  readonly selected: boolean;
  readonly onClick: () => void;
  readonly title?: string | undefined;
  readonly onContextMenu?: ((event: ReactMouseEvent<HTMLButtonElement>) => void) | undefined;
}): JSX.Element {
  const swatchStyle: CSSProperties = {
    position: "relative",
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    background: color,
    border: "1px solid rgba(0,0,0,0.08)",
    cursor: "pointer",
    padding: 0,
    boxShadow: selected ? "0 0 0 3px var(--color-accent, #1e63d4)" : undefined,
    outline: "none",
  };
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={color}
      title={title ?? color}
      onClick={onClick}
      onContextMenu={onContextMenu}
      style={swatchStyle}
    />
  );
}

// 「背景なし」を表す色見本。白地に赤い斜線を引いて「なし」を視覚化する。
export function NoneSwatch({
  label,
  selected,
  onClick,
}: {
  readonly label: string;
  readonly selected: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  const swatchStyle: CSSProperties = {
    position: "relative",
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    // 白地に細い赤の斜線（左下→右上）を引いて「なし」を表現する。
    background:
      "linear-gradient(to top right, transparent calc(50% - 1px), #d22b2b calc(50% - 1px), #d22b2b calc(50% + 1px), transparent calc(50% + 1px)), #fff",
    border: "1px solid rgba(0,0,0,0.18)",
    cursor: "pointer",
    padding: 0,
    boxShadow: selected ? "0 0 0 3px var(--color-accent, #1e63d4)" : undefined,
    outline: "none",
  };
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      title={label}
      onClick={onClick}
      style={swatchStyle}
    />
  );
}

// 「＋」ボタンに見える色追加トリガ。クリックすると明示確定型のエディタを開く。
export function AddColorSwatch({
  expanded,
  onOpen,
}: {
  readonly expanded: boolean;
  readonly onOpen: (anchorElement: HTMLButtonElement) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      title="カスタム色を追加"
      aria-label="カスタム色を追加"
      aria-haspopup="dialog"
      aria-expanded={expanded}
      data-testid="custom-color-add"
      onClick={(event) => onOpen(event.currentTarget)}
      style={{
        position: "relative",
        width: "26px",
        height: "26px",
        borderRadius: "50%",
        border: "1.5px dashed var(--color-border, #b6b6c6)",
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        color: "var(--color-text-muted, #6b6f7d)",
        background: "transparent",
        padding: 0,
      }}
    >
      <Icon d={AddIconData} />
    </button>
  );
}

/**
 * px 入力の即時反映をまとめる trailing debounce の待ち時間（ミリ秒）。
 * ▲▼ を 1 回押して次の操作へ移るまでの間隔より短いため体感では「押した瞬間」に見え、
 * ▲▼ の連打と複数桁の連続打鍵は 1 回の反映へまとまる。
 */
export const TEXT_SIZE_PREVIEW_DEBOUNCE_MS = 150;

/**
 * 文字サイズのプリセットボタン。px の昇順に並べる。
 * この配列と TEXT_SIZE_PRESET_PX の齟齬（種別の追加漏れ）は型で縛れないため、
 * 件数の一致を単体テストで固定する。
 */
const TEXT_SIZE_PRESETS: readonly {
  readonly id: Exclude<TextSizeKind, "custom">;
  readonly label: string;
}[] = [
  { id: "smallest", label: "最小" },
  { id: "tiny", label: "極小" },
  { id: "small", label: "小" },
  { id: "medium", label: "中" },
  { id: "large", label: "大" },
  { id: "huge", label: "特大" },
  { id: "largest", label: "最大" },
];

// 文字サイズの 2 段階目選択 UI。
// 最小・極小・小・中・大・特大・最大 のプリセットボタンと、「px」入力（数値スピン）を
// 横並びで提供する（幅が足りない環境では折り返す）。
// プリセット値と一致する px の場合は該当ボタンを選択状態として強調する。
// px 入力は途中の文字列を local state（draft）で保持しつつ、範囲内（4〜128）の値だけを
// 短い debounce で即時反映する。ここで clamp してはいけない。clamp すると「24」と打つ
// 途中の「2」が下限（=4）として反映され、以降のキーストロークが破棄されてしまうため、
// 利用者が複数桁の値を入力できなくなる。範囲外のまま離れたときの丸めは、
// blur / Enter の確定（commitDraft）だけが行う。
// value が null（値が割れている）ときは px 欄を空文字で初期表示し、プリセットも強調しない。
export function TextSizeChooser({
  value,
  onChange,
}: {
  readonly value: number | null;
  readonly onChange: (px: number) => void;
}): JSX.Element {
  const kind: TextSizeKind | null = value === null ? null : resolveTextSizeKind(value);
  // value（外部）と pxDraft（ローカル文字列）を二重持ちし、外部値の更新があれば draft を
  // 同期する。利用者の途中入力中は draft が外部値より先行する。
  const [pxDraft, setPxDraft] = useState<string>(() => (value === null ? "" : String(value)));
  // 予約した反映が発火する時点の最新値と比べるため、value を ref でも保持する。
  // タイマー内で描画時のクロージャ値を使うと、他参加者の変更で外から値が動いた場合に
  // 古い値と比較して不要な op を送ってしまう。
  const valueRef = useRef<number | null>(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  // 同じ理由で onChange も ref で保持する。予約の発火までに選択対象が変わって
  // ハンドラーが差し替わった場合、打鍵時のクロージャだと古い対象へ適用してしまう。
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const previewTimerRef = useRef<number | null>(null);
  const cancelScheduledPreview = useCallback((): void => {
    if (previewTimerRef.current !== null) {
      window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  }, []);
  // 「即時反映で自分が起こした value の変化」では draft を書き換えない。書き換えると、
  // 入力途中の表記（"04" や前後の空白）が String(value) へ置き換わり、カーソルが末尾へ飛ぶ。
  useEffect(() => {
    // 反映待ちのあいだ（他参加者の変更が割り込んだ場合）も書き換えない。書き換えると、
    // 入力欄が一度だけ相手の値を映してから予約の発火で自分の値へ戻り、表示がちらつく。
    if (previewTimerRef.current !== null) return;
    const parsed = Number.parseInt(pxDraft, 10);
    if (Number.isFinite(parsed) && parsed === value) return;
    setPxDraft(value === null ? "" : String(value));
    // pxDraft は「外部値が変わったときに現在の draft と突き合わせる」ためだけに読むので、
    // 依存へは加えない（加えると打鍵のたびに draft を外部値へ巻き戻してしまう）。
  }, [value]);
  // アンマウント後に onChange が走らないよう、予約を取り消す。
  useEffect(() => cancelScheduledPreview, [cancelScheduledPreview]);

  const commitDraft = (): void => {
    // 確定の直後に古い値の予約が発火して巻き戻るのを防ぐ。
    cancelScheduledPreview();
    // 混在表示（空文字）のまま px 欄をフォーカスして何も入力せず外しただけで、
    // clampTextFontSize(NaN) が既定値へ丸められて全対象が書き換わるのを防ぐ。
    // value が数値のときの挙動（空文字で確定すると既定値へ丸める）は変更しない。
    if (value === null && pxDraft.trim() === "") return;
    const next = clampTextFontSize(Number.parseInt(pxDraft, 10));
    setPxDraft(String(next));
    if (next !== value) onChange(next);
  };

  // 範囲内の値だけを予約する。空・非数値・範囲外は draft のまま保持して反映しない。
  const schedulePreview = (raw: string): void => {
    // 打鍵のたびに、まず古い予約を取り消す。取り消さずに範囲外（"200" の途中）や空へ
    // 変わると、直前の範囲内の値（"20"）が遅れて発火して value が動き、draft 同期の
    // useEffect が入力途中の表記を巻き戻してしまう。
    cancelScheduledPreview();
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return;
    if (parsed < TEXT_SIZE_MIN_PX || parsed > TEXT_SIZE_MAX_PX) return;
    previewTimerRef.current = window.setTimeout(() => {
      previewTimerRef.current = null;
      if (parsed !== valueRef.current) onChangeRef.current(parsed);
    }, TEXT_SIZE_PREVIEW_DEBOUNCE_MS);
  };

  const presetButton = (
    id: Exclude<TextSizeKind, "custom">,
    label: string,
    px: number
  ): JSX.Element => (
    <Button
      key={id}
      type="button"
      variant={kind === id ? "action emphasis" : "action"}
      // 子パネルの選択肢も、メイン・親と同じ正方形の寸法契約へそろえる。
      className="canvas-toolbar-square-button"
      aria-label={`文字サイズ: ${label}（${px}px）`}
      aria-pressed={kind === id}
      title={`${label}（${px}px）`}
      onClick={() => {
        // 文字範囲・表セルのパネルは pointerdown を preventDefault して編集中のフォーカスを
        // 保つため、ここへ来ても px 欄の blur（commitDraft）が走らない。そのため
        // 予約の取り消しと px 欄の表示更新を、このハンドラー自身で行う。
        // 取り消さないと打ちかけの値が遅れて発火してプリセットが巻き戻り、
        // 表示を更新しないと「現在値と同じプリセット」を押したときに外部値が動かず、
        // 打ちかけの表記（例: "200"）が入力欄へ残って実サイズと食い違う。
        cancelScheduledPreview();
        setPxDraft(String(px));
        onChange(px);
      }}
    >
      {/* 「最小」「極小」「特大」「最大」の 2 文字が正方形の枠からはみ出さないよう、
          ラベルだけ 1 段小さくする。ボタン自身の font-size は外寸（2.5em）の基準なので変えない。 */}
      <span style={{ minWidth: "2.4em", textAlign: "center", fontSize: "12px" }}>{label}</span>
    </Button>
  );
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
      {TEXT_SIZE_PRESETS.map((preset) =>
        presetButton(preset.id, preset.label, TEXT_SIZE_PRESET_PX[preset.id])
      )}
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "12px",
          color: "var(--color-text-muted, #6b6f7d)",
        }}
        title="px 指定"
      >
        <input
          type="number"
          inputMode="numeric"
          min={TEXT_SIZE_MIN_PX}
          max={TEXT_SIZE_MAX_PX}
          step={1}
          value={pxDraft}
          aria-label="文字サイズ（px 指定）"
          onChange={(event) => {
            // 途中の入力（例: "200" を打つ途中の "2"）も draft として受け入れる。
            // 範囲内の値だけを debounce 付きで即時反映し、clamp は blur / Enter の確定時に行う。
            const raw = event.currentTarget.value;
            setPxDraft(raw);
            schedulePreview(raw);
          }}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft();
            }
          }}
          style={{
            width: "64px",
            padding: "4px 6px",
            border: "1px solid var(--color-border, #b6b6c6)",
            borderRadius: "var(--radius-1, 4px)",
            font: "inherit",
            color: "inherit",
            background: kind === "custom" ? "var(--color-surface, #fff)" : "transparent",
          }}
        />
        <span>px</span>
      </label>
    </div>
  );
}

// ペンの太さ（細 / 中 / 太）の選択肢。描画前設定に使う。
const PEN_THICKNESS_OPTIONS: readonly {
  readonly value: PenThickness;
  readonly label: string;
}[] = [
  { value: "thin", label: "細" },
  { value: "medium", label: "中" },
  { value: "thick", label: "太" },
];

// ペンの太さ選択 UI。Confluence の例に倣い、太さの異なるドットで 3 段階を表現する。
// ドットは選択中のペン色でプレビューし、色と太さの両方を確認できるようにする。
// value が null（値が割れている・プリセット外）のときは、どの段階も強調しない。
export function PenThicknessChooser({
  value,
  color,
  onChange,
}: {
  readonly value: PenThickness | null;
  readonly color: string;
  readonly onChange: (thickness: PenThickness) => void;
}): JSX.Element {
  // 実際の線幅（PEN_THICKNESS_PIXELS）は細かすぎて見分けづらいため、見やすい大きさへ拡大したドットで表す。
  const dotDiameter = (thickness: PenThickness): number => PEN_THICKNESS_PIXELS[thickness] * 2 + 2;
  const dotColor = isValidHexColor(color) ? color : "var(--color-text)";
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {PEN_THICKNESS_OPTIONS.map((option) => (
        <Button
          key={option.value}
          type="button"
          variant={value === option.value ? "action emphasis" : "action"}
          className="canvas-toolbar-square-button"
          aria-label={`ペンの太さ: ${option.label}`}
          aria-pressed={value === option.value}
          title={`${option.label}（${PEN_THICKNESS_PIXELS[option.value]}px）`}
          onClick={() => onChange(option.value)}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: `${dotDiameter(option.value)}px`,
              height: `${dotDiameter(option.value)}px`,
              borderRadius: "50%",
              background: dotColor,
              // 白系のペン色でもボタン上で視認できるよう、うっすら輪郭を付ける。
              border: "1px solid rgba(0,0,0,0.12)",
            }}
          />
        </Button>
      ))}
    </div>
  );
}

const SHAPE_STROKE_WIDTH_OPTIONS: readonly {
  readonly value: ShapeStrokeWidth;
  readonly label: string;
}[] = [
  { value: "thin", label: "細" },
  { value: "medium", label: "中" },
  { value: "thick", label: "太" },
];

/** 図形の枠線（矢印では線）の太さを、横線プレビューで選ぶ共有 UI。 */
export function ShapeStrokeWidthChooser({
  value,
  color,
  ariaLabelPrefix,
  onChange,
}: {
  readonly value: ShapeStrokeWidth | null;
  readonly color: string;
  readonly ariaLabelPrefix: string;
  readonly onChange: (width: ShapeStrokeWidth) => void;
}): JSX.Element {
  const lineColor = isValidHexColor(color) ? color : "var(--color-text)";
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {SHAPE_STROKE_WIDTH_OPTIONS.map((option) => {
        const px = SHAPE_STROKE_WIDTH_PIXELS[option.value];
        return (
          <Button
            key={option.value}
            type="button"
            variant={value === option.value ? "action emphasis" : "action"}
            className="canvas-toolbar-square-button"
            aria-label={`${ariaLabelPrefix}: ${option.label}`}
            aria-pressed={value === option.value}
            title={`${ariaLabelPrefix}: ${option.label}（${px}px）`}
            onClick={() => onChange(option.value)}
          >
            <span
              aria-hidden="true"
              style={{
                display: "inline-block",
                width: "18px",
                height: `${px}px`,
                borderRadius: "999px",
                background: lineColor,
              }}
            />
          </Button>
        );
      })}
    </div>
  );
}

/** 親項目と子ボタンで共用する線種プレビュー。色に依存せず実線・丸点列を判別できる。 */
export function StrokePatternPreview({
  pattern,
}: {
  readonly pattern: StrokePattern;
}): JSX.Element {
  const width = 3;
  const dotted = pattern === "dotted";
  return (
    <svg aria-hidden="true" width="22" height="12" viewBox="0 0 22 12">
      <line
        x1="1"
        y1="6"
        x2="21"
        y2="6"
        stroke="currentColor"
        strokeWidth={width}
        strokeDasharray={dotted ? strokeDashArray(width, pattern).join(" ") : undefined}
        strokeLinecap={dotted ? "round" : undefined}
      />
    </svg>
  );
}

/** 図形・ペンで共用する実線 / 点線 chooser。 */
export function StrokePatternChooser({
  value,
  onChange,
}: {
  readonly value: StrokePattern | null;
  readonly onChange: (pattern: StrokePattern) => void;
}): JSX.Element {
  const options: readonly { readonly value: StrokePattern; readonly label: string }[] = [
    { value: "solid", label: "実線" },
    { value: "dotted", label: "点線" },
  ];
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {options.map((option) => (
        <Button
          key={option.value}
          type="button"
          variant={value === option.value ? "action emphasis" : "action"}
          className="canvas-toolbar-square-button"
          aria-label={option.label}
          aria-pressed={value === option.value}
          title={option.label}
          onClick={() => onChange(option.value)}
        >
          <StrokePatternPreview pattern={option.value} />
        </Button>
      ))}
    </div>
  );
}

// 文字寄せ（左/中央/右）の選択肢。テキスト・付箋の挿入前設定に使う。
const TEXT_ALIGN_OPTIONS: readonly {
  readonly value: TextAlign;
  readonly label: string;
  readonly icon: string;
}[] = [
  { value: "left", label: "左寄せ", icon: FormatAlignLeftIconData },
  { value: "center", label: "中央寄せ", icon: FormatAlignCenterIconData },
  { value: "right", label: "右寄せ", icon: FormatAlignRightIconData },
];

// 文字寄せ選択 UI。左/中央/右のアイコンボタンを横並びで提供し、選択中を強調する。
// value が null（値が割れている）ときは、どの向きも強調しない。
export function TextAlignChooser({
  value,
  onChange,
}: {
  readonly value: TextAlign | null;
  readonly onChange: (align: TextAlign) => void;
}): JSX.Element {
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {TEXT_ALIGN_OPTIONS.map((option) => (
        <Button
          key={option.value}
          type="button"
          variant={value === option.value ? "action emphasis" : "action"}
          className="canvas-toolbar-square-button"
          aria-label={option.label}
          aria-pressed={value === option.value}
          title={option.label}
          onClick={() => onChange(option.value)}
        >
          <Icon d={option.icon} />
        </Button>
      ))}
    </div>
  );
}
